import { ListResponse, ResponseView } from "@/types/api";
import { api } from "@/store";
import { t } from "@/localization";

export enum PartnerType {
  // For local partners
  photo = "PHOTO",
  epcInspector = "EPC_INSPECTOR",
  electricityInspector = "ELECTRICITY_INSPECTOR",
  fuelOilTankInspector = "FUEL_OIL_TANK_INSPECTOR",
  forSaleSign = "FOR_SALE_SIGN",
  asbestosInspector = "ASBESTOS_INSPECTOR",
  stylist = "STYLIST",
  copywriter = "COPYWRITER",
  comboEpcInspector = "COMBI_EPC_INSPECTOR", // TODO: remove (can the backend still return this?)
  surveyor = "SURVEYOR", // TODO: remove (can the backend still return this?)

  // Only for VGE partnercompany (legacy)
  vgEEpc = "VGE_EPC",
  vgEEk = "VGE_EK",
  vgEFuelOilTank = "VGE_FUEL_OIL_TANK",
  vgEAsbestos = "VGE_ASBESTOS",
  vgEImmoSigns = "VGE_IMMO_SIGNS",
}

export enum PartnerCompany {
  // local is null,
  vge = "VGE",
  aceg = "ACEG",
  inpress = "INPRESS",
}

export enum EpcType {
  EPCAppartment = "epc_appartement",
  EPCClosedBuilding = "epc_geslotenbebouwing",
  EPCsemiDetachedBuilding = "epc_halfopenbebouwing",
  EPCOpenBuilding = "epc_openbebouwing",
  EPCOpenBuilding350 = "epc_openbebouwing_350",
  EPCNonResidential250 = "epc_niet_residentieel_250",
  EPCNonResidential251 = "epc_niet_residentieel_251",
  EPCCommonParts = "epc_gemeenschappelijke_delen",
  EPCNonResidential500 = "epc_niet_residentieel_500",
}
export const epcTypeTranslations = (type: EpcType): string => {
  switch (type) {
    case EpcType.EPCAppartment:
      return t("views.epc_types.epc_appartement");
    case EpcType.EPCClosedBuilding:
      return t("views.epc_types.epc_geslotenbebouwing");
    case EpcType.EPCsemiDetachedBuilding:
      return t("views.epc_types.epc_halfopenbebouwing");
    case EpcType.EPCOpenBuilding:
      return t("views.epc_types.epc_openbebouwing");
    case EpcType.EPCOpenBuilding350:
      return t("views.epc_types.epc_openbebouwing_350");
    case EpcType.EPCNonResidential250:
      return t("views.epc_types.epc_niet_residentieel_250");
    case EpcType.EPCNonResidential251:
      return t("views.epc_types.epc_niet_residentieel_251");
    case EpcType.EPCCommonParts:
      return t("views.epc_types.epc_gemeenschappelijke_delen");
    case EpcType.EPCNonResidential500:
      return t("views.epc_types.epc_niet_residentieel_500");
  }
};

export enum EkType {
  ElectricalInspection = "ek_keuring",
  ElectricalRecheck = "ek_herkeuring",
  ElectricalInspectionNonResidential = "ek_niet_residentieel",
}
export const ekTypeTranslations = (type: EkType): string => {
  switch (type) {
    case EkType.ElectricalInspection:
      return t("views.ek_types.ek_keuring");
    case EkType.ElectricalRecheck:
      return t("views.ek_types.ek_herkeuring");
    case EkType.ElectricalInspectionNonResidential:
      return t("views.ek_types.ek_niet_residentieel");
  }
};
export enum AsbestosType {
  Asbestos = "asbestinventaris",
}
export enum ImmoSignType {
  ImmoSignDefault = "immoborden_standaard",
}

export enum FueltankType {
  FuelOilTankInspectionAboveSurface = "stookolietank_keuring_bovengronds",
  FuelOilTankInspectionBelowSurface = "stookolietank_keuring_ondergronds",
}
export const fueltankTypeTranslations = (type: FueltankType): string => {
  switch (type) {
    case FueltankType.FuelOilTankInspectionAboveSurface:
      return t("views.fueltank_types.stookolietank_keuring_bovengronds");
    case FueltankType.FuelOilTankInspectionBelowSurface:
      return t("views.fueltank_types.stookolietank_keuring_ondergronds");
  }
};
export interface PartnerServiceAddon {
  title: string;
}
export interface PartnerService {
  title: string;
  addons?: PartnerServiceAddon[];
}

export interface PartnerOffice {
  id: number;
  name: string;
  legal_name?: string;
  vat_number?: string;
}

export interface PartnerAccount {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

export interface PartnerInvite {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

export interface PartnerBody {
  company_name: string;
  type: PartnerType;
  office_ids: number[];
  services?: PartnerService[];
  accounts?: Array<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  }>;
}

export interface Partner {
  id: number;
  company_name: string;
  type: PartnerType;
  subtype?: PartnerCompany | null; // null for local
  offices?: PartnerOffice[];
  services?: PartnerService[];
  accounts?: PartnerAccount[];
  invites?: PartnerInvite[];
  sort?: Array<string | number>;
}

export interface PartnerQueryParams {
  perPage?: number;
  office_ids?: number[];
  group_id?: number;
}

export const fetchPartner = async (id: number) => {
  const response = await api.get<ResponseView<Partner>>(`/partners/${id}`, { withCredentials: true });
  return response.data;
};
export const fetchPartners = async (params: PartnerQueryParams) => {
  const response = await api.get<ListResponse<Partner>>("/partners", { params, withCredentials: true }); // TODO backend should return in the same format as the other endpoints
  return response.data;
};

export const createPartner = async (payload: PartnerBody) => {
  const response = await api.post<ResponseView<Partner>>("/partners", payload, { withCredentials: true });
  return response.data;
};

export const updatePartner = async (id: number, payload: Partial<PartnerBody>) => {
  const response = await api.patch<ResponseView<Partner>>(`/partners/${id}`, payload, { withCredentials: true });
  return response.data;
};
